import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { defaultBillingDayOfWeek, getDefaultBillingDayOfYear } from '~/constants';
import { BillingTriggerType } from '~/types/api.model';
import { BillingDayOfMonthModel } from '../BillingDayOfMonth';
import { OneTimeInStagesModel } from '../OneTimeInStagesModel';
import { makeMaxCharges, makeOnApprovalBilling, makeOnDemandBilling, makeOneTimeManualBilling, makeOngoingBilling, makeRepeatableManualBilling } from './makers';
import * as selectors from './selectors';
export function fromDTO(dto, isAwaitingApproval, options) {
    const { recurrencePeriod, billingDayOfMonth, billingDayOfWeek, billingDayOfYear, isBilledUpfront, billUpfrontState, isPaused, isProrated, maxCharges } = dto;
    switch(dto.trigger){
        case 'onApproval':
        case BillingTriggerType.OnApproval:
            return makeOnApprovalBilling();
        case BillingTriggerType.OneTimeManual:
            return makeOneTimeManualBilling();
        case BillingTriggerType.RepeatableManual:
            if (recurrencePeriod !== '' && recurrencePeriod != null) return makeRepeatableManualBilling(recurrencePeriod, {
                maxCharges
            });
            else return null;
        case BillingTriggerType.Ongoing:
            if (recurrencePeriod !== '' && recurrencePeriod != null) return makeOngoingBilling(recurrencePeriod, {
                billingDayOfMonth: billingDayOfMonth && BillingDayOfMonthModel.make(billingDayOfMonth),
                billingDayOfWeek,
                billingDayOfYear,
                isBilledUpfront,
                billUpfrontState: billUpfrontState != null ? billUpfrontState : undefined,
                isProrated,
                isPaused,
                maxCharges,
                isAwaitingApproval,
                startBillingAt: options === null || options === void 0 ? void 0 : options.startBillingAt
            });
            else return null;
        case BillingTriggerType.OneTimeInStages:
            return {
                trigger: BillingTriggerType.OneTimeInStages,
                oneTimeInStages: OneTimeInStagesModel.fromDTO(dto.oneTimeInStages)
            };
        case BillingTriggerType.OnDemand:
            return makeOnDemandBilling(dto.onDemand.preapprovalPeriod, {
                maxCharges
            });
    }
}
export function toDTO(billing) {
    return selectors.match(billing, {
        onApproval: ()=>({
                trigger: BillingTriggerType.OnApproval,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: makeMaxCharges(false),
                recurrencePeriod: ''
            }),
        oneTimeManual: ()=>({
                trigger: BillingTriggerType.OneTimeManual,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: makeMaxCharges(false),
                recurrencePeriod: ''
            }),
        ongoing: (ongoing)=>({
                trigger: BillingTriggerType.Ongoing,
                billingDayOfYear: ongoing.billingDayOfYear,
                isBilledUpfront: ongoing.isBilledUpfront,
                billUpfrontState: ongoing.isBilledUpfront ? ongoing.billUpfrontState : null,
                recurrencePeriod: ongoing.recurrencePeriod,
                billingDayOfMonth: ongoing.billingDayOfMonth ? ongoing.billingDayOfMonth.toJSON() : null,
                billingDayOfWeek: ongoing.billingDayOfWeek,
                isProrated: ongoing.isProrated,
                isPaused: ongoing.isPaused,
                maxCharges: ongoing.maxCharges
            }),
        repeatableManual: (repeatableManual)=>({
                trigger: BillingTriggerType.RepeatableManual,
                billingDayOfMonth: repeatableManual.billingDayOfMonth ? repeatableManual.billingDayOfMonth.toJSON() : null,
                billingDayOfWeek: repeatableManual.billingDayOfWeek,
                billingDayOfYear: repeatableManual.billingDayOfYear,
                isBilledUpfront: repeatableManual.isBilledUpfront,
                recurrencePeriod: repeatableManual.recurrencePeriod,
                maxCharges: repeatableManual.maxCharges,
                isPaused: false,
                isProrated: false
            }),
        oneTimeInStages: (oneTimeInStages)=>({
                trigger: BillingTriggerType.OneTimeInStages,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: makeMaxCharges(false),
                recurrencePeriod: '',
                oneTimeInStages: oneTimeInStages.oneTimeInStages.toDTO()
            }),
        onDemand: (onDemand)=>({
                trigger: BillingTriggerType.OnDemand,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: onDemand.maxCharges,
                recurrencePeriod: '',
                onDemand: {
                    preapprovalPeriod: onDemand.preapprovalPeriod
                }
            })
    });
}
export function toDBServiceBillingTrigger(billing) {
    return selectors.match(billing, {
        onApproval: ()=>({
                type: BillingTriggerType.OnApproval
            }),
        oneTimeManual: ()=>({
                type: BillingTriggerType.OneTimeManual
            }),
        ongoing: (ongoing)=>({
                type: BillingTriggerType.Ongoing,
                ongoing: {
                    billUpfront: ongoing.isBilledUpfront,
                    billUpfrontState: ongoing.billUpfrontState,
                    isPaused: ongoing.isPaused,
                    recurrence: toDBRecurrenceData(ongoing),
                    prorateFirstInvoice: ongoing.isProrated,
                    maxCharges: {
                        enabled: selectors.maxChargesEnabled(ongoing),
                        limit: selectors.maxChargesLimit(ongoing),
                        chargesLeft: selectors.maxChargesLeft(ongoing)
                    }
                }
            }),
        repeatableManual: (repeatableManual)=>({
                type: BillingTriggerType.RepeatableManual,
                repeatableManual: {
                    billUpfront: repeatableManual.isBilledUpfront,
                    recurrence: toDBRecurrenceData(repeatableManual),
                    maxCharges: {
                        enabled: selectors.maxChargesEnabled(repeatableManual),
                        limit: selectors.maxChargesLimit(repeatableManual),
                        chargesLeft: selectors.maxChargesLeft(repeatableManual)
                    }
                }
            }),
        oneTimeInStages: (oneTimeInStages)=>({
                type: BillingTriggerType.OneTimeInStages,
                oneTimeInStages: oneTimeInStages.oneTimeInStages.toDTO()
            }),
        onDemand: (onDemand)=>({
                type: BillingTriggerType.OnDemand,
                onDemand: {
                    preapprovalPeriod: onDemand.preapprovalPeriod,
                    maxCharges: onDemand.maxCharges
                }
            })
    });
}
function toDBRecurrenceData(billing) {
    const { recurrencePeriod } = billing;
    const billingDayOfMonth = billing.billingDayOfMonth || BillingDayOfMonthModel.make();
    const billingDayOfWeek = billing.billingDayOfWeek || defaultBillingDayOfWeek;
    const billingDayOfYear = billing.billingDayOfYear || getDefaultBillingDayOfYear();
    return {
        period: toDBRecurrencePeriod(recurrencePeriod, {
            dayOfMonth: billingDayOfMonth,
            dayOfWeek: billingDayOfWeek,
            dayOfYear: billingDayOfYear
        })
    };
}
export function toDBRecurrencePeriod(period, days) {
    const withoutDay = {
        type: period,
        monthly: null,
        weekly: null,
        biweekly: null,
        yearly: null
    };
    return selectors.matchRecurrencePeriod(period, {
        weekly: ()=>_object_spread_props(_object_spread({}, withoutDay), {
                weekly: {
                    day: days.dayOfWeek
                }
            }),
        biweekly: ()=>_object_spread_props(_object_spread({}, withoutDay), {
                biweekly: {
                    day: days.dayOfWeek
                }
            }),
        monthly: ()=>_object_spread_props(_object_spread({}, withoutDay), {
                monthly: {
                    dayOfMonth: days.dayOfMonth.toDBType()
                }
            }),
        quarterly: ()=>withoutDay,
        yearly: ()=>_object_spread_props(_object_spread({}, withoutDay), {
                yearly: {
                    dayOfYear: days.dayOfYear
                }
            })
    });
}
